import {create} from "zustand";
import {getStorage, setStorage} from "./util.store";

// Currency definitions - synced with API response
//
// For rrc the primary currency is ZAR and USDT stays enabled (matches what the wallet
// returns). Symbols here are only a fallback: components should use the i18n currency* keys.
const currencies = [
  {
    id: "ZAR",
    name: "South African Rand", // Keep for backward compatibility, but components should use translation
    symbol: "R", // Keep for backward compatibility, but components should use translation
    type: "fiat",
    enabled: true,
    isDefault: true,
  },
  {
    id: "USDT",
    name: "Tether USD", // Keep for backward compatibility, but components should use translation
    symbol: "₮", // Updated to match API response
    type: "crypto",
    enabled: true,
    isDefault: false,
  },
  {
    id: "USD",
    name: "US Dollars", // Keep for backward compatibility, but components should use translation
    symbol: "US$", // Disambiguated from CLP
    type: "fiat",
    enabled: false,
    isDefault: false,
  },
  {
    id: "VES",
    name: "Bolívares", // Keep for backward compatibility, but components should use translation
    symbol: "Bs.", // Keep for backward compatibility, but components should use translation
    type: "fiat",
    enabled: false,
    isDefault: false,
  },
  {
    id: "CLP",
    name: "Peso Chileno", // Keep for backward compatibility, but components should use translation
    symbol: "CL$", // Disambiguated from USD
    type: "fiat",
    enabled: false,
    isDefault: false,
  },
  // BTC removed as requested
];

// Helper function to get selected currency from Storage
const getStoredCurrency = () => {
  try {
    const storedCurrencyId = getStorage("selectedCurrency");
    if (storedCurrencyId) {
      const currency = currencies.find(c => c.id === storedCurrencyId && c.enabled);
      if (currency) {
        return currency;
      }
    }
  } catch (_error) {}
  return currencies.find(c => c.isDefault);
};

// Helper function to save selected currency to Storage
const saveSelectedCurrency = currencyId => {
  try {
    setStorage("selectedCurrency", currencyId);
  } catch (_error) {}
};

const useCurrencyStore = create((set, get) => ({
  // State
  currencies: currencies,
  selectedCurrency: getStoredCurrency(),
  isManagementModalVisible: false,

  // Actions
  setSelectedCurrency: currencyId => {
    const currency = get().currencies.find(c => c.id === currencyId);
    if (currency?.enabled) {
      set({selectedCurrency: currency});
      saveSelectedCurrency(currencyId);
    }
  },

  toggleCurrencyEnabled: currencyId => {
    const currencies = get().currencies.map(currency => {
      if (currency.id === currencyId) {
        // Don't allow disabling the default currency
        if (currency.isDefault) {
          return currency;
        }
        return {...currency, enabled: !currency.enabled};
      }
      return currency;
    });

    set({currencies});

    // If the currently selected currency was disabled, switch to default
    const selectedCurrency = get().selectedCurrency;
    if (selectedCurrency && !currencies.find(c => c.id === selectedCurrency.id)?.enabled) {
      const defaultCurrency = currencies.find(c => c.isDefault);
      set({selectedCurrency: defaultCurrency});
      saveSelectedCurrency(defaultCurrency.id);
    }
  },

  showManagementModal: () => set({isManagementModalVisible: true}),
  hideManagementModal: () => set({isManagementModalVisible: false}),

  // Get currency conversion rate (mock implementation)
  getConversionRate: (fromCurrency, toCurrency) => {
    // This would normally fetch from an API
    const mockRates = {
      ZAR: {USD: 0.055, USDT: 0.055},
      USD: {ZAR: 18.2, USDT: 1.0},
      USDT: {ZAR: 18.2, USD: 1.0},
      VES: {USD: 0.027, USDT: 0.027},
      CLP: {USD: 0.001, USDT: 0.001},
    };

    if (fromCurrency === toCurrency) {
      return 1;
    }
    return mockRates[fromCurrency]?.[toCurrency] || 1;
  },

  convertAmount: (amount, fromCurrency, toCurrency) => {
    const rate = get().getConversionRate(fromCurrency, toCurrency);
    return amount * rate;
  },
}));

export default useCurrencyStore;
